import { useState } from "react";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "~/atoms/button";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import {
  LanguageCodeMap,
  type LanguageCode,
  type DocumentDetailsVersion,
  FetchError,
} from "~/api/types";
import { translateDocument } from "~/api/v2/requests";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "~/atoms/dialog";

function getLangLabel(lang: LanguageCode, t: TFunction): string {
  return t(($) => $.commons.languages[lang]);
}

interface TranslateDocumentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: DocumentDetailsVersion;
}

export const TranslateDocumentModal = ({
  open,
  onOpenChange,
  version,
}: TranslateDocumentModalProps) => {
  const { t } = useTranslation();

  const [sourceLanguage, setSourceLanguage] = useState<LanguageCode>(
    LanguageCodeMap.AUTO_DETECT,
  );
  const [targetLanguage, setTargetLanguage] = useState<
    LanguageCode | undefined
  >(undefined);
  const [isTranslating, setIsTranslating] = useState(false);

  const buildLanguageOptions = (excludeLanguages?: LanguageCode[]) => {
    const prioritizedLanguages: LanguageCode[] = [
      LanguageCodeMap.AUTO_DETECT,
      LanguageCodeMap.EN_GB,
      LanguageCodeMap.EN_US,
      LanguageCodeMap.SV,
      LanguageCodeMap.FI,
      LanguageCodeMap.DE,
    ].filter((lang) => !excludeLanguages?.includes(lang));

    const prioritizedOptions = prioritizedLanguages.map((lang) => ({
      value: lang,
      label: getLangLabel(lang, t),
    }));

    const otherOptions = Object.values(LanguageCodeMap)
      .filter(
        (lang) =>
          !prioritizedLanguages.includes(lang) &&
          !excludeLanguages?.includes(lang),
      )
      .map((lang) => ({
        value: lang,
        label: getLangLabel(lang, t),
      }));

    return [
      ...prioritizedOptions,
      { value: "separator", label: "", isSeparator: true },
      ...otherOptions,
    ];
  };

  const handleTranslateDocument = async () => {
    if (!targetLanguage) return;
    setIsTranslating(true);

    try {
      toast.info(t(($) => $.commons.translation.translationStarted));

      const response = await translateDocument(
        version.id,
        targetLanguage,
        sourceLanguage === LanguageCodeMap.AUTO_DETECT
          ? undefined
          : sourceLanguage,
      );
      const blob = await response.blob();
      const languageSuffix = targetLanguage;

      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", `${languageSuffix}_${version.title}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      toast.success(t(($) => $.commons.translation.translationDownloaded));
      handleClose();
    } catch (error) {
      if (error instanceof FetchError) {
        const details = error.details as
          { reason?: string; message?: string } | undefined;

        if (
          error.code === "VALIDATION_ERROR" &&
          details?.reason === "QUOTA_EXCEEDED"
        ) {
          toast.error(t(($) => $.commons.translation.quotaExceededError));
        } else if (
          error.code === "VALIDATION_ERROR" &&
          details?.reason === "TOO_MANY_REQUESTS"
        ) {
          toast.error(t(($) => $.commons.translation.tooManyRequestsError));
        } else if (error.code === "VALIDATION_ERROR" && details?.message) {
          toast.error(details.message);
        } else {
          toast.error(t(($) => $.commons.translation.translationError));
        }
      } else {
        toast.error(t(($) => $.commons.translation.translationError));
      }
    } finally {
      setIsTranslating(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setSourceLanguage(LanguageCodeMap.AUTO_DETECT);
    setTargetLanguage(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t(($) => $.commons.translation.downloadTranslatedFile)}
          </DialogTitle>
          <DialogDescription className="text-left text-sm">
            {t(($) => $.commons.translation.selectLanguageDescription)}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <p className="text-muted-foreground text-sm">
              {t(($) => $.commons.translation.selectSourceLanguage)}
            </p>
            <ControlledCombobox
              value={sourceLanguage}
              onChange={(value) => setSourceLanguage(value as LanguageCode)}
              placeholder={t(($) => $.commons.translation.selectLanguage)}
              options={buildLanguageOptions()}
            />
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-muted-foreground text-sm">
              {t(($) => $.commons.translation.selectTargetLanguage)}
            </p>
            <ControlledCombobox
              value={targetLanguage}
              onChange={(value) => setTargetLanguage(value as LanguageCode)}
              placeholder={t(($) => $.commons.translation.selectLanguage)}
              options={buildLanguageOptions([
                sourceLanguage,
                LanguageCodeMap.AUTO_DETECT,
              ])}
            />
          </div>
        </div>

        {isTranslating && (
          <div className="bg-primary/5 border-primary/5 rounded-md border px-4 py-3">
            <p className="text-sm">
              {t(($) => $.commons.translation.translationStartedInfo)}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button
            onClick={handleTranslateDocument}
            disabled={!targetLanguage || isTranslating}
          >
            {isTranslating ? (
              <>
                <span>{t(($) => $.commons.translation.isTranslating)}</span>
                <div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              </>
            ) : (
              t(($) => $.commons.buttons.download)
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
